// Administration (SPEC §11, admins only): a full-screen sheet with five tabs — Utilisateurs,
// Étages & services, Connecteurs, Moteurs, Journal — plus « Accès Claude (MCP) » and « Données »
// when the server reports its MCP server on (bootstrap features.mcp, docs/wip/SPEC-MCP.md §6) and
// « Activité » when it reports features.activity. Each tab is a view created on first display
// (web/ui/admin/*.js) and kept while the sheet is open, so switching tabs keeps its state; a view
// may open another tab with params (Utilisateurs → « Voir l’activité » of a person). The server
// enforces every rule again (auth: 'admin' on all these endpoints).
import { h, tabs } from '../dom.js';
import { t } from '../i18n/fr.js';
import { createUsersView } from './admin/users.js';
import { createFloorsView } from './admin/floors.js';
import { createConnectorsView } from './admin/connectors.js';
import { createEnginesView } from './admin/engines.js';
import { createMcpView } from './admin/mcp.js';
import { createDataView } from './admin/data.js';
import { createActivityView } from './admin/activity.js';
import { createAuditView } from './admin/audit.js';
import './admin/strings.js';

/** Tab ids, in order (route #/admin/<tab>), without the MCP ones nor « Activité ». */
export const ADMIN_TABS = ['users', 'floors', 'connectors', 'engines', 'audit'];
/** Tabs of the office's MCP server, shown before the Journal when features.mcp is true. */
export const MCP_TABS = ['mcp', 'data'];
/** « Activité », just before the Journal when features.activity is true. */
export const ACTIVITY_TAB = 'activity';

const FACTORIES = {
  users: createUsersView,
  floors: createFloorsView,
  connectors: createConnectorsView,
  engines: createEnginesView,
  mcp: createMcpView,
  data: createDataView,
  activity: createActivityView,
  audit: createAuditView,
};

/**
 * The tabs of the sheet for the bootstrap features (the MCP tabs only when `features.mcp`,
 * « Activité » only when `features.activity`; both before the Journal).
 * @param {{mcp?: boolean, activity?: boolean}|null|undefined} features
 * @returns {string[]}
 */
export function adminTabs(features) {
  const extra = [...(features?.mcp === true ? MCP_TABS : []), ...(features?.activity === true ? [ACTIVITY_TAB] : [])];
  const at = ADMIN_TABS.indexOf('audit');
  return [...ADMIN_TABS.slice(0, at), ...extra, ...ADMIN_TABS.slice(at)];
}

/**
 * A valid tab id among `list` (unknown or hidden → 'users').
 * @param {unknown} tab
 * @param {string[]} [list] the tabs shown (default: without the MCP ones)
 * @returns {string}
 */
export function adminTab(tab, list = ADMIN_TABS) {
  return list.includes(tab) ? tab : 'users';
}

/** Keeps the address bar on #/admin/<tab> without triggering the router (no hashchange). */
function syncHash(tab) {
  try {
    const loc = globalThis.location;
    if (!loc || !String(loc.hash || '').startsWith('#/admin')) return;
    const target = `#/admin/${tab}`;
    if (loc.hash !== target) globalThis.history?.replaceState?.(globalThis.history.state, '', target);
  } catch { /* history unavailable (tests, sandboxed frames) */ }
}

/**
 * Opens the administration sheet (route #/admin[/<tab>]; the router only calls it for admins).
 * @param {object} ctx app context
 * @param {'users'|'floors'|'connectors'|'engines'|'mcp'|'data'|'activity'|'audit'|null} [tab] initial tab
 *   (default 'users'; 'mcp' and 'data' only exist when the server reports features.mcp, 'activity'
 *   when it reports features.activity)
 * @returns {import('./overlay.js').Layer}
 */
export function openAdmin(ctx, tab = 'users') {
  const tabIds = adminTabs(ctx.store.get().features);
  const valid = (id) => adminTab(id, tabIds);
  let active = valid(tab);
  const views = new Map();
  const panels = new Map();
  const tabBar = tabs(tabIds.map((id) => ({ id, label: t(`admin.tabs.${id}`) })), {
    active, label: t('admin.title'), onChange: (id) => show(id, { fromUser: true }),
  });
  tabBar.classList.add('adm-tabs');
  const tabButtons = new Map([...tabBar.childNodes].map((b) => [b.dataset.tab, b]));
  const main = h('div', { class: 'adm-main' });
  const content = h('div', { class: 'adm-shell' }, h('div', { class: 'adm-tabbar' }, tabBar), main);

  function panelFor(id) {
    if (panels.has(id)) return panels.get(id);
    const btn = tabButtons.get(id);
    const panel = h('div', { class: 'adm-panel', role: 'tabpanel', id: `adm-panel-${id}`, tabIndex: -1, 'aria-labelledby': btn?.id || null });
    btn?.setAttribute('aria-controls', panel.id);
    panels.set(id, panel);
    main.appendChild(panel);
    return panel;
  }

  // What a view may do with the sheet: open another tab, with params for that tab's view.
  const nav = {
    has: (id) => tabIds.includes(id),
    open: (id, params = null) => {
      if (!tabIds.includes(id)) return false;
      show(id, { fromUser: true, params });
      panels.get(active)?.focus?.({ preventScroll: true });
      return true;
    },
  };

  function show(id, { fromUser = false, params = null } = {}) {
    active = valid(id);
    tabBar.setActive(active);
    for (const [pid, panel] of panels) panel.hidden = pid !== active;
    const panel = panelFor(active);
    panel.hidden = false;
    if (!views.has(active)) {
      const view = FACTORIES[active](ctx, { ...nav, params });
      views.set(active, view);
      panel.replaceChildren(view.el);
    } else if (params && views.get(active).applyParams) {
      views.get(active).applyParams(params);
    } else if (fromUser) {
      // Coming back to a tab: refresh quietly (other admins may have changed things).
      views.get(active).reload?.();
    }
    syncHash(active);
  }

  const layer = ctx.overlay.openSheet({
    title: t('admin.title'),
    eyebrow: t('adm.eyebrow'),
    content,
    className: 'adm-sheet',
  });

  show(active);

  const offs = [
    // After an SSE reconnection (server restart, network), reload what the open tab shows.
    ctx.events.on('hello', () => views.get(active)?.reload?.()),
  ];
  layer.onClose(() => {
    for (const off of offs) off();
    for (const view of views.values()) {
      try { view.destroy?.(); } catch (err) { console.error(err); }
    }
    views.clear();
  });
  return layer;
}
